/**
 * DiscoveryPage component - main page for user discovery and swiping
 */

import type { PublicUserProfile } from "@fitties/shared";
import logger from "@logger";

import { PageLoading } from "@shared/components/PageLoading";
import useCurrentUser from "@shared/queries/useCurrentUser";
import { motion } from "framer-motion";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useSwipeUser } from "../mutations";
import { useDiscoveryUsers } from "../queries/useDiscoveryUsers";
import { resolveDiscoveryUserId } from "../services/discoveryService";
import { useDiscoveryStore } from "../stores/discoveryStore";
import type { DiscoveryPageProps, Match, SwipeAction } from "../types";
import { DiscoveryPageCelebrationLayer } from "./DiscoveryMatchCelebrationOverlay";
import { preloadMatchCelebrationAssets, preloadProfilePhotosForCelebration } from "./matchCelebrationPreload";
import { SwipeStack } from "./SwipeStack";
import { useDiscoveryPageCelebration } from "./useDiscoveryPageCelebration";
import { usePreloadMatchCelebration } from "./usePreloadMatchCelebration";

/**
 * DiscoveryPage component for the main discovery/swiping interface
 *
 * @example
 * ```tsx
 * <DiscoveryPage currentUserId="user-123" />
 * ```
 */
export function DiscoveryPage({ currentUserId, startWithUserId }: DiscoveryPageProps) {
  const {
    currentUsers,
    isLoading,
    error,
    setCurrentUsers,
    setLoading,
    setError,
    addSwipedUser,
    addMatch,
    swipedUsers,
  } = useDiscoveryStore();

  const [showCelebration, setShowCelebration] = useState(false);
  const [matchData, setMatchData] = useState<{
    match: Match;
    matchedUserId: string;
    matchedUser?: PublicUserProfile;
  } | null>(null);

  const {
    data: users,
    isLoading: usersLoading,
    error: usersError,
  } = useDiscoveryUsers(currentUserId, 20, startWithUserId);
  const { user: viewer, isLoading: viewerLoading } = useCurrentUser();
  const swipeInteractionLocked = viewerLoading || !viewer;

  const stackUsers = users ?? currentUsers;

  const displayStackUsers = useMemo(() => {
    if (!stackUsers?.length) {
      return stackUsers;
    }
    const skip = new Set(swipedUsers);
    if (matchData?.matchedUserId) {
      skip.add(matchData.matchedUserId);
    }
    return stackUsers.filter((u) => {
      const id = resolveDiscoveryUserId(u);
      if (!id || id === currentUserId) {
        return false;
      }
      return !skip.has(id);
    });
  }, [stackUsers, swipedUsers, matchData?.matchedUserId, currentUserId]);

  usePreloadMatchCelebration();
  const { celebrationPair, matchUsersLoading } = useDiscoveryPageCelebration(currentUserId, showCelebration, matchData);

  const resolveMatchedUserFromStack = (matchedUserId: string): PublicUserProfile | undefined => {
    const source = stackUsers ?? currentUsers;
    return source.find((u) => resolveDiscoveryUserId(u) === matchedUserId);
  };

  // Swipe mutation - recordSwipe already handles mutual matches atomically
  const swipeMutation = useSwipeUser({
    onSuccess: (match, variables) => {
      logger.debug("🎯 Swipe mutation onSuccess called", {
        hasMatch: !!match,
        match,
        variables,
        swipedUserId: variables.swipedUserId,
      });

      if (match) {
        const matchedUser = resolveMatchedUserFromStack(variables.swipedUserId);
        preloadMatchCelebrationAssets();
        preloadProfilePhotosForCelebration(viewer?.photo_url, matchedUser?.photo_url);
        logger.debug("🎉🎉🎉 MATCH DETECTED IN UI - Setting up celebration! 🎉🎉🎉", {
          chatId: match.id,
          users: match.users,
          matchedUserId: variables.swipedUserId,
          hasMatchedUserSnapshot: !!matchedUser,
        });
        addMatch(match);
        setMatchData({
          match,
          matchedUserId: variables.swipedUserId,
          ...(matchedUser ? { matchedUser } : {}),
        });
        setShowCelebration(true);
        logger.debug("✅ Celebration state set to true", {
          showCelebration: true,
          matchData: { match, matchedUserId: variables.swipedUserId },
        });
      } else {
        logger.debug("❌ No match in onSuccess callback", {
          match,
          variables,
        });
      }
    },
    onError: (error) => {
      console.error("❌ Swipe mutation error", error);
      setError(error.message);
    },
  });

  /**
   * Handle user swipe
   */
  const handleSwipe = async (userId: string, direction: SwipeAction) => {
    if (direction === "right") {
      preloadMatchCelebrationAssets();
      const swipedCard = stackUsers?.find((u) => resolveDiscoveryUserId(u) === userId);
      preloadProfilePhotosForCelebration(viewer?.photo_url, swipedCard?.photo_url);
    }

    // Optimistically add to swipedUsers BEFORE mutation to prevent card mismatch
    addSwipedUser(userId);

    try {
      logger.debug("👆 User swiped", {
        userId,
        direction,
        currentUserId,
        isRightSwipe: direction === "right",
      });

      // Record the swipe - recordSwipe already handles mutual match detection atomically
      const matchResult = await swipeMutation.mutateAsync({
        swiperId: currentUserId,
        swipedUserId: userId,
        direction,
      });

      logger.debug("📝 Swipe mutation completed", {
        hasMatch: !!matchResult,
        matchResult,
        userId,
        direction,
      });
    } catch (error) {
      logger.error("❌ Error in handleSwipe", error);
      setError(error instanceof Error ? error.message : "Failed to record swipe");
    }
  };

  /**
   * Handle match creation
   */
  const handleMatch = (match: Match) => {
    addMatch(match);
  };

  /**
   * Handle celebration close
   */
  const handleCelebrationClose = () => {
    setShowCelebration(false);
    setMatchData(null);
  };

  /**
   * Handle retry
   */
  const handleRetry = () => {
    setError(null);
    // The SwipeStack will handle resetting the index
  };

  useLayoutEffect(() => {
    if (users) {
      logger.debug("DiscoveryPage: Setting users in store", { usersCount: users.length });
      setCurrentUsers(users);
    }
  }, [users, setCurrentUsers]);

  const isPageLoading = usersLoading && users === undefined;

  useEffect(() => {
    if (isPageLoading && currentUsers.length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isPageLoading, currentUsers.length, setLoading]);

  // Update error state
  useEffect(() => {
    if (usersError) {
      setError(usersError.message);
    }
  }, [usersError, setError]);

  const celebrationOverlay = (
    <DiscoveryPageCelebrationLayer
      currentUserId={currentUserId}
      showCelebration={showCelebration}
      matchData={matchData}
      celebrationPair={celebrationPair}
      onClose={handleCelebrationClose}
    />
  );

  useEffect(() => {
    logger.debug("🎭 Celebration state changed", {
      showCelebration,
      matchData,
      matchUsersLoading,
      hasCelebrationPair: !!celebrationPair,
    });
  }, [showCelebration, matchData, matchUsersLoading, celebrationPair]);

  if (isPageLoading || (isLoading && currentUsers.length === 0)) {
    return (
      <>
        <div className="flex min-h-0 flex-1 flex-col">
          <PageLoading message="Finding people for you..." />
        </div>
        {celebrationOverlay}
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        <div className="flex min-h-0 flex-1 items-center justify-center bg-gray-50 p-8">
          <div className="text-center">
            <h2 className="mb-2 font-mono text-xl font-semibold text-gray-900">Something went wrong</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              type="button"
              onClick={handleRetry}
              className="bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
              data-testid="retry-button"
            >
              Try Again
            </button>
          </div>
        </div>
        {celebrationOverlay}
      </>
    );
  }

  // Empty state (stack exhausted — celebration must still render after a match on the last card)
  if (!displayStackUsers || displayStackUsers.length === 0) {
    return (
      <>
        <div className="flex min-h-0 flex-1 items-center justify-center bg-gray-50 p-8">
          <div className="text-center">
            <h2 className="mb-2 font-mono text-xl font-semibold text-gray-900">You've seen everyone!</h2>
            <p className="text-gray-600 mb-6">Check back later for new profiles, or try adjusting your preferences.</p>
            <button
              type="button"
              onClick={handleRetry}
              className="bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
              data-testid="refresh-button"
            >
              Refresh
            </button>
          </div>
        </div>
        {celebrationOverlay}
      </>
    );
  }

  // Debug logging for store state (can be removed in production)
  logger.debug("DiscoveryPage: Store state", {
    currentUsersCount: displayStackUsers.length,
    isLoading,
    error,
  });

  // Main discovery interface
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex min-h-0 w-full flex-1 flex-col overflow-hidden"
        data-testid="discovery-page"
      >
        {swipeInteractionLocked ? (
          <p
            className="shrink-0 border-b border-lavender-200 bg-white/90 px-4 py-2 text-center font-mono text-[length:var(--font-size-body-small)] font-semibold text-lavender-800"
            data-testid="discovery-access-preview-banner"
          >
            Same queue as everyone — unlock to swipe and match.
          </p>
        ) : null}
        <SwipeStack
          key={startWithUserId ? `start-with-${startWithUserId}` : "default"}
          users={displayStackUsers}
          interactionLocked={swipeInteractionLocked}
          onSwipe={handleSwipe}
          onMatch={handleMatch}
        />
      </motion.div>
      {celebrationOverlay}
    </>
  );
}
